import React from 'react';
import { i18n } from 'config/locales';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnError, CommonOnUpdate, CommonRequired, CommonScrollIntoView
} from 'types/props';
import { ForeignBornDocumentModel } from 'forms/shared/types';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import * as formTypes from 'constants/formTypes';
import ValidationElement from '../ValidationElement';
import { alphaNumericRegEx } from '../../../validators/helpers';
import RadioGroup from '../RadioGroup';
import Radio from '../Radio';
import Textarea from '../Textarea';
import Text from '../Text';
import Show from '../Show';
import Field from '../Field';
import DateControl from '../DateControl';
import NotApplicable from '../NotApplicable';

interface Props extends Partial<ForeignBornDocumentModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  scrollIntoView?: CommonScrollIntoView
  required?: CommonRequired
  requireExpirationDate?: boolean
  attribute?: string
  formType?: string
}

export default class ForeignBornDocuments extends ValidationElement<Props> {
  update = (queue: Partial<ForeignBornDocumentModel>) => {
    this.props.onUpdate({
      DocumentType: this.props.DocumentType,
      OtherExplanation: this.props.OtherExplanation,
      DocumentNumber: this.props.DocumentNumber,
      DocumentExpiration: this.props.DocumentExpiration,
      DocumentExpirationNotApplicable: this.props.DocumentExpirationNotApplicable,
      Comments: this.props.Comments,
      ...queue,
    });
  };

  updateDocumentType = (values: ForeignBornDocumentModel['DocumentType']) => {
    let applicableFlag = this.props.DocumentExpirationNotApplicable?.applicable;

    if (['I-551',
      'I-766',
      'I-94',
      'Visa',
      'NonImmigrantStudent',
      'ExchangeVisitor',
    ].includes(values.value ?? 'None') && this.props.formType === formTypes.SF86) {
      applicableFlag = true;
    }

    this.update({
      DocumentType: values,
      DocumentExpirationNotApplicable: { name: 'DocumentExpirationNotApplicable', applicable: applicableFlag },
    });
  };

  updateOtherExplanation = (values: ForeignBornDocumentModel['OtherExplanation']) => {
    this.update({
      OtherExplanation: values,
    });
  };

  updateDocumentNumber = (values: ForeignBornDocumentModel['DocumentNumber']) => {
    this.update({
      DocumentNumber: values,
    });
  };

  updateDocumentExpiration = (values: ForeignBornDocumentModel['DocumentExpiration']) => {
    this.update({
      DocumentExpiration: values,
    });
  };

  updateDocumentExpirationNotApplicable = (
    values: ForeignBornDocumentModel['DocumentExpirationNotApplicable']
  ) => {
    this.update({
      DocumentExpirationNotApplicable: values,
    });
  };

  updateComments = (values: ForeignBornDocumentModel['Comments']) => {
    this.update({
      Comments: values,
    });
  };

  render() {
    const { requireExpirationDate, attribute } = this.props;
    return (
      <div className="foreign-born-documents">
        <Field
          help="relationships.civilUnion.help.foreignBornDocument"
          title={i18n.t('relationships.civilUnion.heading.foreignBornDocument')}
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="p"
          attribute={`${attribute}.DocumentType`}
        >
          {[formTypes.SF85P, formTypes.SF85PS].includes(this.props.formType || '') && (
            <div>
              <RadioGroup
                name="born"
                className="option-list option-list-vertical"
                selectedValue={(this.props.DocumentType || {}).value}
                required={this.props.required}
                onError={this.props.onError}
                attribute={`${attribute}.DocumentType`}
              >
                <Radio
                  className="born"
                  label={i18n.t('foreignBornDocuments.bornToUSParents.label.fs240')}
                  value="FS240"
                  onUpdate={this.updateDocumentType}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
                <Radio
                  className="born"
                  label={i18n.t('foreignBornDocuments.bornToUSParents.label.ds1350')}
                  value="DS1350"
                  onUpdate={this.updateDocumentType}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
                <Radio
                  className="born"
                  label={i18n.t('foreignBornDocuments.sf85p.label.citizenship')}
                  value="CitizenshipCertificate"
                  onUpdate={this.updateDocumentType}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
                <Radio
                  className="born"
                  label={i18n.t('foreignBornDocuments.sf85p.label.passport')}
                  value="Passport"
                  onUpdate={this.updateDocumentType}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
                <Radio
                  className="born"
                  label={i18n.t('foreignBornDocuments.sf85p.label.alien')}
                  value="AlienRegistrationSF85P"
                  onUpdate={this.updateDocumentType}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
                <Radio
                  className="born"
                  label={i18n.t('foreignBornDocuments.sf85p.label.certificateOfNaturalization')}
                  value="CertificateOfNaturalization"
                  onUpdate={this.updateDocumentType}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
              </RadioGroup>
              <label id="born-none">{i18n.t('foreignBornDocuments.para.none')}</label>
              <RadioGroup
                name="none"
                className="option-list option-list-vertical"
                selectedValue={(this.props.DocumentType || {}).value}
                required={this.props.required}
                onError={this.props.onError}
                attribute={`${attribute}.DocumentType`}
              >
                <Radio
                  className="none"
                  label={i18n.t('foreignBornDocuments.none.label.none')}
                  value="None"
                  onUpdate={this.updateDocumentType}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
              </RadioGroup>
            </div>
          )}
          {![formTypes.SF85P, formTypes.SF85PS].includes(this.props.formType || '') && (
            <div>
              <label id="born-us">{i18n.t('foreignBornDocuments.para.bornToUSParents')}</label>
              <RadioGroup
                name="born"
                className="option-list option-list-vertical"
                selectedValue={(this.props.DocumentType || {}).value}
                required={this.props.required}
                onError={this.props.onError}
                attribute={`${attribute}.DocumentType`}
              >
                <Radio
                  className="born"
                  label={i18n.t('foreignBornDocuments.bornToUSParents.label.fs240')}
                  value="FS240"
                  onUpdate={this.updateDocumentType}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
                <Radio
                  className="born"
                  label={i18n.t('foreignBornDocuments.bornToUSParents.label.ds1350')}
                  value="DS1350"
                  onUpdate={this.updateDocumentType}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
              </RadioGroup>

              <label id="born-naturalized">{i18n.t('foreignBornDocuments.para.naturalized')}</label>
              <RadioGroup
                name="naturalized"
                className="option-list option-list-vertical"
                selectedValue={(this.props.DocumentType || {}).value}
                required={this.props.required}
                onError={this.props.onError}
                attribute={`${attribute}.DocumentType`}
              >
                <Radio
                  className="naturalized alien"
                  label={i18n.t('foreignBornDocuments.naturalized.label.alien')}
                  value="AlienRegistration"
                  onUpdate={this.updateDocumentType}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
                <Radio
                  className="naturalized permanent"
                  label={i18n.t('foreignBornDocuments.naturalized.label.permanentResident')}
                  value="PermanentResident"
                  onUpdate={this.updateDocumentType}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
                <Radio
                  className="naturalized certificate"
                  label={i18n.t(
                    'foreignBornDocuments.naturalized.label.certificateOfNaturalization'
                  )}
                  value="CertificateOfNaturalization"
                  onUpdate={this.updateDocumentType}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
              </RadioGroup>

              <label id="born-derived">{i18n.t('foreignBornDocuments.para.derived')}</label>
              <RadioGroup
                name="derived"
                className="option-list option-list-vertical"
                selectedValue={(this.props.DocumentType || {}).value}
                required={this.props.required}
                onError={this.props.onError}
                attribute={`${attribute}.DocumentType`}
              >
                <Radio
                  className="derived alien"
                  label={i18n.t('foreignBornDocuments.derived.label.alien')}
                  value="DerivedAlienRegistration"
                  onUpdate={this.updateDocumentType}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
                <Radio
                  className="derived permanent"
                  label={i18n.t('foreignBornDocuments.derived.label.permanentResident')}
                  value="DerivedPermanentResident"
                  onUpdate={this.updateDocumentType}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
                <Radio
                  className="derived certificate"
                  label={i18n.t('foreignBornDocuments.derived.label.certificateOfNaturalization')}
                  value="DerivedCertificateOfNaturalization"
                  onUpdate={this.updateDocumentType}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
              </RadioGroup>

              <label id="not-citizen">{i18n.t('foreignBornDocuments.para.notCitizen')}</label>
              <RadioGroup
                name="notCitizen"
                className="option-list option-list-vertical"
                selectedValue={(this.props.DocumentType || {}).value}
                required={this.props.required}
                onError={this.props.onError}
                attribute={`${attribute}.DocumentType`}
              >
                <Radio
                  className="notcitizen permanent"
                  label={i18n.t('foreignBornDocuments.notCitizen.label.permanentResident')}
                  value="I-551"
                  onUpdate={this.updateDocumentType}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
                <Radio
                  className="notcitizen employment"
                  label={i18n.t('foreignBornDocuments.notCitizen.label.employmentAuthorization')}
                  value="I-766"
                  onUpdate={this.updateDocumentType}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
                <Radio
                  className="notcitizen arrival"
                  label={i18n.t('foreignBornDocuments.notCitizen.label.arrivalDepartureRecord')}
                  value="I-94"
                  onUpdate={this.updateDocumentType}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
                <Radio
                  className="notcitizen visa"
                  label={i18n.t('foreignBornDocuments.notCitizen.label.visa')}
                  value="Visa"
                  onUpdate={this.updateDocumentType}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
                <Radio
                  className="notcitizen student"
                  label={i18n.t('foreignBornDocuments.notCitizen.label.nonImmigrantStudent')}
                  value="NonImmigrantStudent"
                  onUpdate={this.updateDocumentType}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
                <Radio
                  className="notcitizen exchange"
                  label={i18n.t('foreignBornDocuments.notCitizen.label.exchangeVisitor')}
                  value="ExchangeVisitor"
                  onUpdate={this.updateDocumentType}
                  onError={this.props.onError}
                  onBlur={this.props.onBlur}
                  onFocus={this.props.onFocus}
                />
              </RadioGroup>
            </div>
          )}
          <label id="born-other">{i18n.t('foreignBornDocuments.para.other')}</label>
          <RadioGroup
            name="other"
            className="option-list option-list-vertical"
            selectedValue={(this.props.DocumentType || {}).value}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.DocumentType`}
          >
            <Radio
              className="other"
              label={i18n.t('foreignBornDocuments.other.label.other')}
              value="Other"
              onUpdate={this.updateDocumentType}
              onError={this.props.onError}
              onBlur={this.props.onBlur}
              onFocus={this.props.onFocus}
            />
          </RadioGroup>

          <Show
            when={
              this.props.DocumentType?.value === 'Other'
              || this.props.DocumentType?.value === 'None'
            }
          >
            <Field attribute={`${attribute}.OtherExplanation`}>
              <Textarea
                name="otherExplanation"
                className="other-explanation"
                label="Provide explanation"
                {...this.props.OtherExplanation}
                onUpdate={this.updateOtherExplanation}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.OtherExplanation`}
              />
            </Field>
          </Show>
        </Field>

        <Field
          title={i18n.t('foreignBornDocuments.heading.documentNumber')}
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="labels"
          attribute={`${attribute}.DocumentNumber`}
        >
          <Text
            name="documentNumber"
            label="Document Number"
            {...this.props.DocumentNumber}
            className="foreign-born-document-number"
            maxlength="30"
            pattern={alphaNumericRegEx}
            prefix="alphanumeric"
            onUpdate={this.updateDocumentNumber}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.DocumentNumber`}
          />
        </Field>
        <Show
          when={['I-551',
            'I-766',
            'I-94',
            'Visa',
            'NonImmigrantStudent',
            'ExchangeVisitor',
          ].includes(this.props.DocumentType?.value ?? 'None')}
        >
          {requireExpirationDate && (
            <Field
              title={i18n.t('foreignBornDocuments.heading.documentExpiration')}
              scrollIntoView={this.props.scrollIntoView}
              adjustFor="labels"
              attribute={`${attribute}.DocumentExpiration`}
            >
              <DateControl
                name="documentExpiration"
                noMaxDate
                {...this.props.DocumentExpiration}
                onUpdate={this.updateDocumentExpiration}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.DocumentExpiration`}
              />
            </Field>
          )}
        </Show>

        <Show
          when={!['I-551',
            'I-766',
            'I-94',
            'Visa',
            'NonImmigrantStudent',
            'ExchangeVisitor',
          ].includes(this.props.DocumentType?.value ?? 'None')}
        >
          {requireExpirationDate && (
            <Field
              title={i18n.t('foreignBornDocuments.heading.documentExpiration')}
              scrollIntoView={this.props.scrollIntoView}
              adjustFor="labels"
              attribute={`${attribute}.DocumentExpiration`}
            >
              <NotApplicable
                name="DocumentExpirationNotApplicable"
                {...this.props.DocumentExpirationNotApplicable}
                label={i18n.t('reference.label.idkNa')}
                or={i18n.m('reference.para.or')}
                onUpdate={this.updateDocumentExpirationNotApplicable}
                attribute={`${attribute}.DocumentExpirationNotApplicable`}
              >
                <DateControl
                  name="documentExpiration"
                  noMaxDate
                  {...this.props.DocumentExpiration}
                  onUpdate={this.updateDocumentExpiration}
                  onError={this.props.onError}
                  required={this.props.required}
                  attribute={`${attribute}.DocumentExpiration`}
                />
              </NotApplicable>
            </Field>
          )}
        </Show>
        <Field
          title={i18n.t('relationships.comments.optional')}
          attribute={`${attribute}Comments`}
          titleSize="label"
          scrollIntoView={this.props.scrollIntoView}
          optional
        >
          <Textarea
            title="Optional Comments"
            name={`${attribute}Comments`}
            className="optional-comment"
            attribute={`${attribute}Comments`}
            {...this.props.Comments}
            onUpdate={this.updateComments}
            required={false}
          />
        </Field>
      </div>
    );
  }
}

ForeignBornDocuments.defaultProps = {
  DocumentType: {},
  OtherExplanation: {},
  DocumentNumber: {},
  DocumentExpiration: {},
  DocumentExpirationNotApplicable: { applicable: true },
  Comments: {},
  onUpdate: () => { },
  onError: (value, arr: ErrorCode[]) => arr,
  requireExpirationDate: true,
};
